<template>
  <div v-if="!accessedMemberInfo.name" class="login_box">
    <TransitionGroup name="fade" tag="div">
      <div v-if="!showPanel.isSignUp">
        <img
          class="login_box_img" src="@/assets/images/WaitingMission.png"
          alt="waiting mission"
        >
        <div class="input_box email">
          <label for="loginEmail">
            <i class="xi-mail-o" />
          </label>
          <input
            id="loginEmail" type="text"
            placeholder="EMAIL"
            :disabled="disabledQualification()"
            v-model="loginForm.email" @keydown.enter.prevent="submitLogin"
            @input="inputValueAni"
          >
        </div>
        <div class="input_box">
          <label for="loginPassword">
            <i class="xi-lock-o" />
          </label>
          <input
            id="loginPassword" type="password"
            placeholder="PASSWORD"
            :disabled="disabledQualification()"
            v-model="loginForm.password" @keydown.enter.prevent="submitLogin"
            @input="inputValueAni"
          >
        </div>
        <div class="remember_box">
          <div>
            <input
              id="rememberMe" type="checkbox"
              :disabled="disabledQualification()"
              v-model="isRememberChecked"
            >
            <label for="rememberMe">
              <span>
                Stay Signed in
              </span>
            </label>
          </div>
          <button
            type="button"
            :disabled="disabledQualification()"
            @click="openForgotAccount"
          >
            Forgot Account
          </button>
        </div>
        <button
          class="sign_in_btn" type="button"
          :disabled="disabledQualification()"
          @click="submitLogin"
        >
          Sign in
        </button>
        <div class="sign_up_btn">
          Don't have an account?
          <button
            type="button"
            :disabled="disabledQualification()"
            @click="showSignupToggle"
          >
            Sign up
            <i class="xi-angle-right-min" />
          </button>
        </div>
      </div>
      <Signup v-else />
    </TransitionGroup>
  </div>
</template>

<script lang="ts" setup>
import { reactive, ref } from 'vue';
import { storeToRefs } from 'pinia';

import { AccessMemberInfoType } from '@/interface/member';
import Signup from '@/components/common/Signup.vue';
import useCommonStore from '@/store/storeCommon';
import useMapStore from '@/store/storeMap';
import useMemberStore from '@/store/storeMember';

const {
  isLoading,
  isMobile,
  isMainLogo,
} = storeToRefs(useCommonStore());
const {
  inputValueAni,
  disabledQualification,
  showPanel,
  showModal,
  showSignupToggle,
  modalContents,
} = useCommonStore();
const { accessLogin, accessedMemberInfo } = useMemberStore();
const { getSatelliteDataApi } = useMapStore();

const isRememberChecked = ref<boolean>(false); // 'Remember me' 체크 유무

// 로그인 할 유저 정보
const loginForm: AccessMemberInfoType = reactive({
  email: '',
  password: '',
});

/**
 * 로그인 기능을 실행합니다.
 */
const submitLogin = async (): Promise<void> => {
  try {
    isLoading.value = true;

    await accessLogin(loginForm, isRememberChecked.value);
    await getSatelliteDataApi();

    const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    if (accessToken) {
      Object.assign(loginForm, {
        email: '',
        password: '',
      });
    } else {
      showModal.isFail = true;
      isMainLogo.value = true;

      Object.assign(modalContents, {
        title: 'Login Failed',
        description: '아이디 또는 패스워드\n정보가 일치하지 않습니다.',
      });
    }
  } catch (e) {
    console.log(e);
  } finally {
    isLoading.value = false;

    if (isMobile && accessedMemberInfo.name && showPanel.isMobileAccountInfo) {
      showPanel.isMobileAccountInfo = false;
    }
    if (!isMobile.value && accessedMemberInfo.name && !showPanel.isAccountInfo) {
      showPanel.isAccountInfo = true;
    }
  }
};

/**
 * 'Forget Account' 모달창을 엽니다.
 */
const openForgotAccount = (): void => {
  isMainLogo.value = true;
  showModal.isForgotAccount = true;
};
</script>

<style scoped lang="scss">
@import '@/assets/css/_colors.scss';

.login_box {
  width: 258.7px;
  height: 352px;
  background: url("@/assets/images/LoginBox.png") no-repeat center;
  background-size: contain;
  padding: 38px 29px 33.2px;
  color: $color-white;
  pointer-events: auto;
  position: relative;

  > div {
    height: 100%;
  }

  .fade-enter-active {
    transition: all 0.3s ease-out;
    transition-delay: 0.3s;
  }

  .fade-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
  }

  .login_box_img {
    margin-bottom: 10px;
  }

  .remember_box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 9.3px;
    margin: 17.5px 0;

    > div {
      display: flex;
      align-items: center;

      input[type="checkbox"] {
        display: none;

        & + label {
          display: flex;
          align-items: center;

          > span {
            padding-left: 7px;
          }

          &:before {
            content: "";
            display: inline-block;
            width: 11.2px;
            height: 11.2px;
            border: 1px solid #707070;
            position: relative;
          }
        }

        &:checked {
          & ~ label {
            color: $color-debra;
          }

          & + label::before {
            content: '';
            background-image: url('@/assets/images/Icons/CheckBtn.svg');
            background-repeat: no-repeat;
            background-position: 50%;
          }
        }
      }
    }
  }

  .sign_in_btn {
    background: $color-debra;
    width: 100%;
    line-height: 25px;
    border-radius: 5px;
    font-size: 10.2px;
    font-weight: bold;
    color: #2a2a2a;
  }

  .sign_up_btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    font-size: 9.3px;
    margin-top: 10px;

    button {
      display: flex;
      align-items: center;
      font-weight: bold;
      font-size: 10px;

      i {
        font-size: 17px;
      }
    }
  }
}
</style>